import React, { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TRAINING_MODULES, TrainingModule, GREEN, GREEN_DIM, GREEN_BORDER, BLUE, PURPLE, AMBER, AMBER_DIM, AMBER_BORDER, inter, mono, poppins } from "@/lib/trainingModules";
import { captureException } from "@/lib/sentry";

const SURFACE = "#111111";
const SURFACE2 = "#161616";
const BORDER = "#1e1e1e";


const SHAKE_KEYFRAMES = `
@keyframes _stb_shake {
  0%,100% { transform: translateX(0); }
  15%      { transform: translateX(-5px); }
  30%      { transform: translateX(5px); }
  45%      { transform: translateX(-5px); }
  60%      { transform: translateX(5px); }
  75%      { transform: translateX(-3px); }
  90%      { transform: translateX(3px); }
}
@keyframes _stb_fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

const MODULES = TRAINING_MODULES;

const TOTAL = MODULES.length;
const LS_CLICKED_KEY = "studio-training-clicked-links";
const LS_DISMISSED_KEY = "studio-training-dismissed";

function loadClickedLinks(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_CLICKED_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch { return new Set(); }
}
function saveClickedLinks(set: Set<string>): void {
  try { localStorage.setItem(LS_CLICKED_KEY, JSON.stringify(Array.from(set))); } catch { /* intentionally silent */ }
}

function ModuleRow({
  mod, index, isCompleted, isLocked, isExpanded, isCurrent, token,
  onToggleExpand, onToggleComplete, isSaving, clickedLinks, onActionClick,
}: {
  mod: TrainingModule; index: number; isCompleted: boolean; isLocked: boolean;
  isExpanded: boolean; isCurrent: boolean; token: string;
  onToggleExpand: () => void; onToggleComplete: () => void;
  isSaving: boolean; clickedLinks: Set<string>; onActionClick: (key: string) => void;
}) {
  const [shaking, setShaking] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const shakeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notifTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (shakeTimer.current) clearTimeout(shakeTimer.current);
    if (notifTimer.current) clearTimeout(notifTimer.current);
  }, []);

  const visitedCount = mod.actions.filter((_, i) => clickedLinks.has(`${mod.id}:${i}`)).length;
  const allVisited = visitedCount >= mod.actions.length;

  const handleMarkComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCompleted) { onToggleComplete(); return; }
    if (!allVisited) {
      setShaking(false);
      requestAnimationFrame(() => {
        setShaking(true); setShowNotif(true);
        if (shakeTimer.current) clearTimeout(shakeTimer.current);
        shakeTimer.current = setTimeout(() => setShaking(false), 620);
        if (notifTimer.current) clearTimeout(notifTimer.current);
        notifTimer.current = setTimeout(() => setShowNotif(false), 3500);
      });
      return;
    }
    onToggleComplete();
  };

  const cardBg = isCompleted ? GREEN_DIM : isLocked ? "#0a0a0a" : isCurrent ? `${mod.color}08` : SURFACE;
  const cardBorder = isCompleted ? GREEN_BORDER : isLocked ? "#161616" : isCurrent ? mod.colorBorder : BORDER;

  return (
    <div
      data-testid={`training-module-${mod.id}`}
      data-locked={isLocked ? "true" : "false"}
      data-completed={isCompleted ? "true" : "false"}
      style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, overflow: "hidden", transition: "border-color 0.2s, background 0.2s", opacity: isLocked ? 0.55 : 1 }}
    >
      <div
        onClick={isLocked ? undefined : onToggleExpand}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: isLocked ? "not-allowed" : "pointer", userSelect: "none" }}
      >
        <div style={{
          width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
          background: isCompleted ? GREEN : isLocked ? "#1a1a1a" : `${mod.color}20`,
          border: `2px solid ${isCompleted ? GREEN : isLocked ? "#2a2a2a" : mod.colorBorder}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: isCompleted ? "#000" : isLocked ? "#444" : mod.color,
          fontSize: isCompleted ? 12 : isLocked ? 12 : 10, fontWeight: 700, transition: "all 0.2s",
        }}>
          {isCompleted ? "✓" : isLocked ? "🔒" : index + 1}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            <span style={{ fontSize: 16, filter: isLocked ? "grayscale(1)" : "none" }}>{mod.icon}</span>
            <span style={{ ...poppins, fontSize: 14, fontWeight: 700, color: isLocked ? "#444" : "#fff" }}>{mod.title}</span>
            {isCompleted && <span style={{ ...mono, fontSize: 8, fontWeight: 700, background: `${GREEN}20`, color: GREEN, border: `1px solid ${GREEN}40`, padding: "2px 6px", borderRadius: 4, letterSpacing: "0.1em" }}>DONE</span>}
            {isLocked && <span style={{ ...mono, fontSize: 8, fontWeight: 700, background: "#1a1a1a", color: "#555", border: "1px solid #2a2a2a", padding: "2px 6px", borderRadius: 4, letterSpacing: "0.1em" }}>LOCKED</span>}
            {isCurrent && !isCompleted && !isLocked && <span style={{ ...mono, fontSize: 8, fontWeight: 700, background: `${mod.color}18`, color: mod.color, border: `1px solid ${mod.colorBorder}`, padding: "2px 6px", borderRadius: 4, letterSpacing: "0.1em" }}>UP NEXT</span>}
          </div>
          <div style={{ ...inter, fontSize: 12, color: isLocked ? "#333" : "#6b7280", marginTop: 1 }}>
            {isLocked ? `Complete module ${index} first` : mod.subtitle}
          </div>
        </div>
        {!isLocked && (
          <span style={{ color: "#6b7280", fontSize: 14, flexShrink: 0, transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "none" }}>▾</span>
        )}
      </div>

      {isExpanded && !isLocked && (
        <div style={{ padding: "0 16px 16px" }}>
          <div style={{ height: 1, background: BORDER, marginBottom: 14 }} />
          <div style={{ ...inter, fontSize: 11, color: "#9ca3af", marginBottom: 8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>What you'll learn</div>
          <ol style={{ margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: 7 }}>
            {mod.lessons.map((lesson, i) => (
              <li key={i} style={{ ...inter, fontSize: 12, color: "#d1d5db", lineHeight: 1.6 }}>{lesson}</li>
            ))}
          </ol>

          {mod.actions.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ ...inter, fontSize: 11, color: "#9ca3af", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Go do it</div>
                <div style={{ ...mono, fontSize: 9, color: allVisited ? GREEN : AMBER, background: allVisited ? `${GREEN}18` : `${AMBER}18`, border: `1px solid ${allVisited ? GREEN_BORDER : AMBER_BORDER}`, padding: "1px 6px", borderRadius: 4, letterSpacing: "0.06em" }}>
                  {visitedCount}/{mod.actions.length} visited
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {mod.actions.map((action, i) => {
                  const key = `${mod.id}:${i}`;
                  const visited = clickedLinks.has(key);
                  return (
                    <a
                      key={i}
                      href={action.href(token)}
                      onClick={() => onActionClick(key)}
                      data-testid={`training-action-${mod.id}-${i}`}
                      data-visited={visited ? "true" : "false"}
                      style={{ ...inter, display: "inline-flex", alignItems: "center", gap: 5, background: visited ? `${GREEN}14` : mod.colorDim, border: `1px solid ${visited ? GREEN_BORDER : mod.colorBorder}`, borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: visited ? GREEN : mod.color, textDecoration: "none", transition: "background 0.15s, border-color 0.15s, color 0.15s" }}
                    >
                      {visited && <span style={{ fontSize: 10, fontWeight: 700 }}>✓</span>}
                      {action.label} →
                    </a>
                  );
                })}
              </div>
              {!allVisited && (
                <div style={{ ...inter, fontSize: 11, color: "#6b7280", marginTop: 7, lineHeight: 1.5 }}>
                  Visit all links above to unlock the mark complete button.
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10 }}>
            {showNotif && (
              <div style={{ ...inter, fontSize: 11, color: AMBER, background: AMBER_DIM, border: `1px solid ${AMBER_BORDER}`, borderRadius: 7, padding: "6px 10px", display: "flex", alignItems: "center", gap: 5, animation: "_stb_fadeIn 0.2s ease" }}>
                ⚠️ Visit all {mod.actions.length} link{mod.actions.length !== 1 ? "s" : ""} first — {visitedCount}/{mod.actions.length} done
              </div>
            )}
            <button
              onClick={handleMarkComplete}
              disabled={isSaving}
              data-testid={`training-btn-complete-${mod.id}`}
              style={{
                ...inter, display: "inline-flex", alignItems: "center", gap: 6,
                background: isCompleted ? `${GREEN}20` : allVisited ? GREEN : `${GREEN}30`,
                border: `1px solid ${isCompleted ? GREEN_BORDER : allVisited ? GREEN : GREEN_BORDER}`,
                borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 700,
                color: isCompleted ? GREEN : allVisited ? "#000" : GREEN,
                cursor: isSaving ? "wait" : "pointer", letterSpacing: "0.04em",
                opacity: isSaving ? 0.7 : 1, transition: "all 0.15s", flexShrink: 0,
                animation: shaking ? "_stb_shake 0.62s ease" : "none", minHeight: 36,
              }}
            >
              {isSaving ? "Saving…" : isCompleted ? "✓ Mark Incomplete" : "✓ Mark Complete"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function StudioTrainingBanner({ token }: { token: string }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [expandedModId, setExpandedModId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [clickedLinks, setClickedLinks] = useState<Set<string>>(() => loadClickedLinks());
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_DISMISSED_KEY) === "1"; } catch { return false; }
  });

  const { data, isLoading } = useQuery<{ completed: string[] }>({
    queryKey: ["training-progress", token],
    queryFn: async () => {
      const res = await fetch("/api/admin/training-progress", {
        headers: { "X-Admin-Token": token },
      });
      if (!res.ok) return { completed: [] };
      return res.json();
    },
    enabled: !!token,
    staleTime: 60_000,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: async ({ moduleId, completed }: { moduleId: string; completed: boolean }) => {
      const res = await fetch("/api/admin/training-progress", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({ moduleId, completed }),
      });
      if (!res.ok) {
        const _fetchErr = new Error("Failed to save");
        captureException(_fetchErr);
        throw _fetchErr;
      }
    },
    onMutate: async ({ moduleId, completed: completedFlag }) => {
      setSavingId(moduleId);
      await queryClient.cancelQueries({ queryKey: ["training-progress", token] });
      const prev = queryClient.getQueryData<{ completed: string[] }>(["training-progress", token]);
      queryClient.setQueryData(["training-progress", token], (old: { completed: string[] } | undefined) => ({
        completed: completedFlag
          ? Array.from(new Set([...(old?.completed ?? []), moduleId]))
          : (old?.completed ?? []).filter((id: string) => id !== moduleId),
      }));
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["training-progress", token], ctx.prev);
    },
    onSettled: () => {
      setSavingId(null);
      queryClient.invalidateQueries({ queryKey: ["training-progress", token] });
    },
  });

  const completed = data?.completed ?? [];
  const completedCount = completed.length;
  const allDone = completedCount >= TOTAL;
  const progressPct = TOTAL > 0 ? Math.round((completedCount / TOTAL) * 100) : 0;

  const currentModule = MODULES.find((m, i) => {
    if (completed.includes(m.id)) return false;
    if (i === 0) return true;
    return completed.includes(MODULES[i - 1].id);
  });

  const handleToggleComplete = useCallback((moduleId: string) => {
    mutation.mutate({ moduleId, completed: !completed.includes(moduleId) });
  }, [completed, mutation]);

  const handleActionClick = useCallback((key: string) => {
    setClickedLinks(prev => {
      const next = new Set(prev);
      next.add(key);
      saveClickedLinks(next);
      return next;
    });
  }, []);

  const handleDismiss = () => {
    try { localStorage.setItem(LS_DISMISSED_KEY, "1"); } catch { /* intentionally silent */ }
    setDismissed(true);
  };

  if (isLoading || dismissed) return null;

  if (allDone) {
    return (
      <div
        data-testid="studio-training-complete-banner"
        style={{ position: "sticky", top: 0, zIndex: 30, background: "#04150e", borderBottom: `2px solid ${GREEN_BORDER}` }}
      >
        <style>{SHAKE_KEYFRAMES}</style>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "12px 20px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>🎓</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...poppins, fontSize: 13, fontWeight: 700, color: GREEN }}>Platform Training Complete!</div>
            <div style={{ ...inter, fontSize: 12, color: "#6b7280", marginTop: 1 }}>You've finished all {TOTAL} training modules. Your studio is fully set up.</div>
          </div>
          <button
            data-testid="training-banner-dismiss"
            onClick={handleDismiss}
            style={{ ...inter, background: GREEN_DIM, border: `1px solid ${GREEN_BORDER}`, borderRadius: 7, padding: "6px 14px", fontSize: 12, fontWeight: 700, color: GREEN, cursor: "pointer", flexShrink: 0, minHeight: 36 }}
          >
            Got it ✓
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="studio-training-banner"
      style={{ position: "sticky", top: 0, zIndex: 30, background: SURFACE, borderBottom: `2px solid ${AMBER_BORDER}` }}
    >
      <style>{SHAKE_KEYFRAMES}</style>

      {/* Compact bar — always visible */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
        <button
          data-testid="training-banner-toggle"
          onClick={() => setExpanded(v => !v)}
          style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "11px 0", display: "flex", alignItems: "center", gap: 14, textAlign: "left" }}
        >
          <span style={{ fontSize: 16, flexShrink: 0 }}>🎓</span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5, overflow: "hidden" }}>
              <span style={{ ...poppins, fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>Platform Training</span>
              <span style={{ ...mono, fontSize: 10, color: "#6b7280" }}>{completedCount}/{TOTAL} modules</span>
              {currentModule && (
                <span style={{ ...inter, fontSize: 12, color: currentModule.color, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
                  Up next: {currentModule.icon} {currentModule.title}
                </span>
              )}
            </div>
            <div style={{ height: 4, background: SURFACE2, borderRadius: 99, overflow: "hidden", border: `1px solid ${BORDER}`, maxWidth: 400 }}>
              <div style={{ height: "100%", width: `${progressPct}%`, background: `linear-gradient(90deg, ${BLUE}, ${PURPLE})`, borderRadius: 99, transition: "width 0.4s ease" }} />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <span style={{ ...mono, fontSize: 11, color: "#6b7280" }}>{progressPct}%</span>
            <span style={{ color: "#6b7280", fontSize: 14, transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "none" }}>▾</span>
          </div>
        </button>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${BORDER}`, maxHeight: "72vh", overflowY: "auto" }}>
          <div style={{ maxWidth: 760, margin: "0 auto", padding: "20px 20px 28px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <div style={{ ...mono, fontSize: 10, color: "#4b5563", letterSpacing: "0.08em" }}>STUDIO TRAINING HUB</div>
                <div style={{ ...poppins, fontSize: 18, fontWeight: 800, color: "#fff", margin: "4px 0 2px" }}>Platform Training</div>
                <div style={{ ...inter, fontSize: 12, color: "#6b7280" }}>Complete each module to finish setup. {TOTAL - completedCount} module{TOTAL - completedCount !== 1 ? "s" : ""} remaining.</div>
              </div>
              <div style={{ ...poppins, fontSize: 26, fontWeight: 800, color: "#fff", textAlign: "center", background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "8px 14px" }}>
                <span data-testid="training-banner-count">{completedCount}/{TOTAL}</span>
                <div style={{ ...inter, fontSize: 10, color: "#6b7280", fontWeight: 400 }}>done</div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {MODULES.map((mod, i) => {
                const isCompleted = completed.includes(mod.id);
                const isLocked = i > 0 && !completed.includes(MODULES[i - 1].id);
                const isCurrent = !isCompleted && !isLocked && MODULES.slice(0, i).every(m => completed.includes(m.id));
                return (
                  <ModuleRow
                    key={mod.id}
                    mod={mod}
                    index={i}
                    isCompleted={isCompleted}
                    isLocked={isLocked}
                    isExpanded={expandedModId === mod.id}
                    isCurrent={isCurrent}
                    token={token}
                    onToggleExpand={() => { if (!isLocked) setExpandedModId(expandedModId === mod.id ? null : mod.id); }}
                    onToggleComplete={() => { if (!isLocked) handleToggleComplete(mod.id); }}
                    isSaving={savingId === mod.id}
                    clickedLinks={clickedLinks}
                    onActionClick={handleActionClick}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
